/**
 * Support Staff Cost Calculator
 * 
 * Calculates the per-minute cost of support staff based on their hourly pay rates
 * and available hours, for inclusion in the salon pricing calculations.
 */

/**
 * Calculate total support staff cost per minute
 * 
 * @param {Array} staffList - List of all staff members
 * @param {number} weeksPerYear - Number of working weeks per year
 * @returns {number} Support staff cost per minute
 */
export const calculateSupportStaffCostPerMinute = (staffList, weeksPerYear = 52) => {
  if (!staffList || !Array.isArray(staffList) || staffList.length === 0) {
    return 0;
  }
  
  // Filter to only support staff
  const supportStaff = staffList.filter(staff => 
    staff.staff_type === 'support' && 
    parseFloat(staff.hourly_pay) > 0 && 
    parseFloat(staff.available_hours) > 0
  );
  
  if (supportStaff.length === 0) {
    return 0;
  }
  
  // Calculate weekly cost for all support staff
  let totalWeeklyCost = 0;
  
  supportStaff.forEach(staff => {
    const hourlyRate = parseFloat(staff.effective_hourly_rate || staff.hourly_pay) || 0;
    const availableHours = parseFloat(staff.available_hours) || 0;
    
    // Weekly cost for this staff member
    const weeklyCost = hourlyRate * availableHours;
    totalWeeklyCost += weeklyCost;
  });
  
  // Convert to annual and then per-minute cost
  const totalAnnualCost = totalWeeklyCost * weeksPerYear;
  const totalRevenueMinutesPerYear = calculateTotalRevenueMinutesPerYear(staffList, weeksPerYear);
  
  // If no revenue minutes, avoid division by zero
  if (totalRevenueMinutesPerYear === 0) {
    return 0;
  }
  
  // Calculate per-minute cost
  return totalAnnualCost / totalRevenueMinutesPerYear;
};

/**
 * Calculate total revenue minutes per year
 * This represents the total number of bookable minutes across all revenue-generating staff
 * 
 * @param {Array} staffList - List of all staff members
 * @param {number} weeksPerYear - Number of working weeks per year
 * @returns {number} Total revenue minutes per year
 */
export const calculateTotalRevenueMinutesPerYear = (staffList, weeksPerYear = 52) => {
  if (!staffList || !Array.isArray(staffList) || staffList.length === 0) {
    return 0;
  }
  
  // Filter to only revenue staff
  const revenueStaff = staffList.filter(staff => 
    staff.staff_type === 'revenue' && 
    parseFloat(staff.available_hours) > 0
  );
  
  if (revenueStaff.length === 0) {
    return 0;
  }
  
  // Calculate total available hours per week for all revenue staff
  let totalWeeklyHours = 0;
  
  revenueStaff.forEach(staff => {
    const availableHours = parseFloat(staff.available_hours) || 0;
    totalWeeklyHours += availableHours;
  });
  
  // Convert to minutes per year
  return totalWeeklyHours * 60 * weeksPerYear;
};

/**
 * Calculate annual overhead costs from the overhead table entries
 * 
 * @param {Array} overheadItems - List of overhead items from the overhead table
 * @returns {number} Total annual overhead cost
 */
export const calculateAnnualOverheadFromItems = (overheadItems) => {
  if (!overheadItems || !Array.isArray(overheadItems) || overheadItems.length === 0) {
    return 0;
  }
  
  let annualTotal = 0;
  
  overheadItems.forEach(item => {
    const amount = parseFloat(item.amount) || 0;
    const regularity = item.regularity ? item.regularity.toLowerCase() : 'monthly';
    
    switch (regularity) {
      case 'daily':
        annualTotal += amount * 365;
        break;
      case 'weekly':
        annualTotal += amount * 52;
        break;
      case 'monthly':
        annualTotal += amount * 12;
        break;
      case 'quarterly':
        annualTotal += amount * 4;
        break;
      case 'yearly':
      case 'annual':
        annualTotal += amount;
        break;
      default:
        // Default to monthly if unknown
        annualTotal += amount * 12;
    }
  });
  
  return annualTotal;
};

export default {
  calculateSupportStaffCostPerMinute,
  calculateTotalRevenueMinutesPerYear,
  calculateAnnualOverheadFromItems
};